<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { Plus, Delete } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Schedule {
  opening_hours: {
    open: string;
    close: string;
  };
}

interface Props {
  modelValue?: Schedule[] | { [key: string]: any };
  defaultStartTime?: string;
  defaultEndTime?: string;
  startTimeLabel?: string;
  endTimeLabel?: string;
  addButtonText?: string;
  emptyStateText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  defaultStartTime: '09:00',
  defaultEndTime: '17:00',
  startTimeLabel: '',
  endTimeLabel: '',
  addButtonText: '',
  emptyStateText: ''
});

const emit = defineEmits<{
  'update:modelValue': [value: Schedule[] | { [key: string]: any }];
}>();

const startTimeLabel = computed(() => props.startTimeLabel || t('settings.pos.general_settings.opening_schedules.start_time'));
const endTimeLabel = computed(() => props.endTimeLabel || t('settings.pos.general_settings.opening_schedules.end_time'));
const addButtonText = computed(() => props.addButtonText || t('settings.pos.general_settings.opening_schedules.add_schedule'));
const emptyStateText = computed(() => props.emptyStateText || t('settings.pos.general_settings.opening_schedules.no_schedules_configured'));

const getSchedulesArray = (value: any): Schedule[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  
  if (typeof value === 'object') {
    if (value['']) {
      if (Array.isArray(value[''])) return value[''];
      if (typeof value[''] === 'object') return getSchedulesArray(value['']);
    }
    if (value.opening_schedules && Array.isArray(value.opening_schedules)) return value.opening_schedules;
    if (Object.keys(value).length === 0) return [];
  }
  
  return [];
};

const schedules = ref<Schedule[]>(getSchedulesArray(props.modelValue));

watch(() => props.modelValue, (newValue) => {
  schedules.value = getSchedulesArray(newValue);
}, { deep: true });

const isValidSchedule = (schedule: any): schedule is Schedule => {
  return schedule?.opening_hours?.open && schedule?.opening_hours?.close;
};

const sortedSchedules = computed(() => {
  return [...schedules.value]
    .filter(isValidSchedule)
    .sort((a, b) => a.opening_hours.open.localeCompare(b.opening_hours.open));
});

const addSchedule = () => {
  schedules.value.push({
    opening_hours: {
      open: props.defaultStartTime,
      close: props.defaultEndTime
    }
  });
  handleScheduleChange();
};

const removeSchedule = (sortedIndex: number) => {
  const schedule = sortedSchedules.value[sortedIndex];
  const originalIndex = schedules.value.indexOf(schedule);
  if (originalIndex > -1) {
    schedules.value.splice(originalIndex, 1);
    handleScheduleChange();
  }
};

const handleScheduleChange = () => {
  const validSchedules = schedules.value.filter(isValidSchedule);
  schedules.value = validSchedules;
  
  if (Array.isArray(props.modelValue)) {
    emit('update:modelValue', validSchedules);
  } else if (typeof props.modelValue === 'object' && props.modelValue && Object.keys(props.modelValue).length > 0) {
    emit('update:modelValue', { '': validSchedules });
  } else {
    emit('update:modelValue', validSchedules);
  }
};
</script>

<template>
  <div class="time-range-widget" style="width: 80%; max-width: 600px;">
    <div v-if="sortedSchedules.length === 0" class="text-gray-500 text-center py-4">
      {{ emptyStateText }}
    </div>

    <div v-for="(schedule, index) in sortedSchedules" :key="index" class="schedule-item mb-2 p-3 border border-gray-200 rounded-lg">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 flex-1">
          <el-time-picker
            v-model="schedule.opening_hours.open"
            format="HH:mm"
            value-format="HH:mm"
            :placeholder="startTimeLabel"
            @change="handleScheduleChange"
            size="small"
            style="width: 120px"
          />
          <span class="text-gray-500">-</span>
          <el-time-picker
            v-model="schedule.opening_hours.close"
            format="HH:mm"
            value-format="HH:mm"
            :placeholder="endTimeLabel"
            @change="handleScheduleChange"
            size="small"
            style="width: 120px"
          />
        </div>
        <el-button @click="removeSchedule(index)" text :icon="Delete" size="small" />
      </div>
    </div>

    <div class="w-full text-center mt-4">
      <el-button @click="addSchedule" type="primary" size="small">
        <el-icon><Plus /></el-icon>
        {{ addButtonText }}
      </el-button>
    </div>
  </div>
</template>

<style scoped>
:deep(.el-time-picker input::selection) {
  background-color: transparent !important;
}

:deep(.el-time-picker input::-moz-selection) {
  background-color: transparent !important;
}
</style>
